/* eslint-disable @typescript-eslint/no-explicit-any */
import styled, { x } from '@xstyled/styled-components';
import React, { Children, Component } from 'react';
import { t } from 'ttag';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import {
  AUDIO,
  HORIZONTAL_AUDIO_PLAYER,
  EMAIL,
  IMAGE,
  VIDEO
} from '../itemIds';

interface Props {
  from: string;
  subject: string;
}

const Header = styled.divBox`
  background-color: greyLight;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  height: 46px;
  padding-top: 13px;
  text-align: center;

  > b {
    font-family: Raleway;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 150%;
  }
`;

const Body = styled.divBox`
  font-family: Raleway;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 150%;
  color: black;
  font-weight: 100;
  & strong {
    font-weight: 700;
    font-size: 17px;
  }
`;

const DotsBlock = styled.div`
  width: 60px;
  height: 30px;
  position: absolute;
  margin-left: 20px;
  padding-top: 5px;
  > div:nth-child(1) {
    background-color: #f7326d;
  }
  > div:nth-child(2) {
    background-color: #fdd15f;
  }
  > div:nth-child(3) {
    background-color: #00a88a;
  }
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  float: left;
  margin-right: 4px;
`;

const Dots = () => {
  return (
    <DotsBlock>
      <Dot />
      <Dot />
      <Dot />
    </DotsBlock>
  );
};

const Attachments = styled.divBox`
  margin-top: 20px;
  > b {
    font-size: 12px;
  }
  > div {
    float: left;
    width: 100%;
    height: 71px;
    margin-right: 18px;
    margin-top: 11px;
  }
  & .fileName {
    font-family: Raleway;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
  }
`;

export type EmailDefinition = ItemDefinition<typeof EMAIL, Props>;

export class Email extends Item<typeof EMAIL, Props> {
  protected render({ children }: RenderProps<Props>) {
    const childArray = Children.toArray(children);

    const getFileName = (c: any, i: number) => {
      let fileExtension = '.jpeg';

      if (c?.props?.type == VIDEO) {
        fileExtension = '.mp4';
      }

      if (c?.props?.type == AUDIO) {
        fileExtension = '.mp3';
      }

      if (c?.props?.type == HORIZONTAL_AUDIO_PLAYER) {
        return null;
      }

      return (
        <span className="fileName">{`${c?.props?.type}${i}${fileExtension}`}</span>
      );
    };

    const attachmentsExist = childArray.find((c: any) => {
      if (
        c.props.type == IMAGE ||
        c.props.type == AUDIO ||
        c.props.type == VIDEO ||
        c.props.type == HORIZONTAL_AUDIO_PLAYER
      ) {
        return true;
      }
    });

    return (
      <Flex variant="center">
        <Card
          backgroundColor="white"
          w={{ _: '330px', sm: '330px', lg: '400px' }}
          paddingBottom="20px"
          minH="300px"
          display="flex"
          flexDirection="column"
          position="relative"
          color="greyDark"
          overflow="hidden"
          className="emailStyleForDarkTheme"
        >
          <Header>
            <Dots />
            <x.b>{t`You've got mail!`}</x.b>
          </Header>
          <x.div p="20px">
            <Body>
              {childArray.map((c: Component<RenderProps<void>>, i) => {
                if (
                  c.props.type == IMAGE ||
                  c.props.type == VIDEO ||
                  c.props.type == AUDIO ||
                  c.props.type == HORIZONTAL_AUDIO_PLAYER
                ) {
                  return false;
                }
                return <x.div key={i}>{c}</x.div>;
              })}
              {attachmentsExist && (
                <Attachments>
                  <x.b style={{ display: 'block', clear: 'both' }}>
                    {t`Attachments`}:
                  </x.b>
                  {childArray.map((c: any, i) => {
                    if (
                      c.props.type != IMAGE &&
                      c.props.type != AUDIO &&
                      c.props.type != VIDEO &&
                      c.props.type != HORIZONTAL_AUDIO_PLAYER
                    ) {
                      return false;
                    }
                    return (
                      <x.div key={i}>
                        {c}
                        {getFileName(c, i)}
                      </x.div>
                    );
                  })}
                </Attachments>
              )}
            </Body>
          </x.div>
        </Card>
      </Flex>
    );
  }
}

registry[EMAIL] = Email;
